import React, { useState, useEffect } from 'react';
import {
  getQuoteLineCertificate,
  uploadQuoteLineCertificate,
  downloadQuoteLineCertificate,
  deleteQuoteLineCertificate,
} from '../api';
import useModalBehavior from '../hooks/useModalBehavior';
import './CertificateModal.css';

const CertificateModal = ({ line, isOpen, onClose, onUpload }) => {
  const [certificateInfo, setCertificateInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  // Load certificate info when modal opens
  useEffect(() => {
    if (isOpen && line) {
      loadCertificateInfo();
    }
  }, [isOpen, line]);

  const loadCertificateInfo = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getQuoteLineCertificate(line.id);
      setCertificateInfo(response.data);
    } catch (err) {
      console.error('Error loading certificate info:', err);
      setError('Failed to load certificate information');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setError(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file to upload');
      return;
    }

    setUploading(true);
    setError(null);
    try {
      await uploadQuoteLineCertificate(line.id, selectedFile);
      if (onUpload) {
        onUpload(selectedFile.name);
      }
      await loadCertificateInfo();
      setSelectedFile(null);
    } catch (err) {
      console.error('Error uploading certificate:', err);
      setError('Failed to upload certificate');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await downloadQuoteLineCertificate(line.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', certificateInfo?.filename || 'certificate.pdf');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading certificate:', err);
      setError('Failed to download certificate');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this certificate?')) {
      return;
    }

    setDeleting(true);
    setError(null);
    try {
      await deleteQuoteLineCertificate(line.id);
      await loadCertificateInfo();
    } catch (err) {
      console.error('Error deleting certificate:', err);
      setError('Failed to delete certificate');
    } finally {
      setDeleting(false);
    }
  };

  const handleCancel = () => {
    setSelectedFile(null);
    setError(null);
    onClose();
  };

  const panelRef = useModalBehavior({ isOpen, onClose: handleCancel });

  if (!isOpen) {
    return null;
  }

  return (
    <div className="certificate-modal-overlay" onClick={handleCancel}>
      <div
        className="certificate-modal"
        onClick={(e) => e.stopPropagation()}
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="certificate-modal-title"
      >
        <div className="certificate-modal-header">
          <h3 id="certificate-modal-title">Certificate for {line?.name || 'Quote Line'}</h3>
          <button className="close-button" onClick={handleCancel} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="certificate-modal-body">
          {loading ? (
            <div className="certificate-loading">Loading certificate information...</div>
          ) : (
            <>
              {certificateInfo?.has_certificate ? (
                <div className="certificate-info">
                  <div className="certificate-exists">
                    <div className="certificate-icon">📄</div>
                    <div className="certificate-details">
                      <strong>Current Certificate:</strong>
                      <p>{certificateInfo.filename}</p>
                    </div>
                  </div>
                  <div className="certificate-actions">
                    <button
                      className="btn btn-secondary"
                      onClick={handleDownload}
                      disabled={deleting}
                    >
                      📥 Download
                    </button>
                    <button
                      className="btn btn-danger"
                      onClick={handleDelete}
                      disabled={deleting}
                    >
                      {deleting ? 'Deleting...' : '🗑️ Delete'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="certificate-empty">
                  <p>No certificate uploaded for this line.</p>
                </div>
              )}

              <div className="certificate-upload-section">
                <h4>Upload New Certificate</h4>
                <div className="file-input-wrapper">
                  <input
                    type="file"
                    id="certificate-file"
                    onChange={handleFileSelect}
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    disabled={uploading}
                  />
                  <label htmlFor="certificate-file" className="file-input-label">
                    {selectedFile ? selectedFile.name : 'Choose a file...'}
                  </label>
                </div>
                {selectedFile && (
                  <button
                    className="btn btn-primary"
                    onClick={handleUpload}
                    disabled={uploading}
                  >
                    {uploading ? 'Uploading...' : '⬆️ Upload Certificate'}
                  </button>
                )}
              </div>

              {error && <div className="certificate-error">{error}</div>}
            </>
          )}
        </div>

        <div className="certificate-modal-footer">
          <button className="btn btn-secondary" onClick={handleCancel}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CertificateModal;
